<template>
    <div>
        <div class="row my-4">
            <div class="col-12">
                <h1 class="text-3xl ps-2">
                    <span v-if="!isInvoice && !payInvoiceableLines">
                        <strong>Swish</strong> {{ $t('innskudd') }}
                    </span>
                    <span v-if="isInvoice || payInvoiceableLines">
                        <strong>Swish</strong>{{ $t('betaling') }}
                    </span>
                </h1>
            </div>
            <div class="col-12">
                <div class="card">
                    <div class="card-body" v-if="!paymentCompleted">
                        <div v-if="isInvoice">
                            <div class="form-group row justify-content-between align-bottom mb-4">
                                <div class="col-md-8 col-6">
                                    <label class="my-md-0">{{ $t('Betaling gjelder' )}}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4 text-end">
                                    <span class="fw-bold">{{ invoiceTypeAndOrderNumber }}</span>
                                </div>
                            </div>
                            <div class="form-group row justify-content-between align-bottom mb-4">
                                <div class="col-md-8 col-6">
                                    <label class="my-md-0">{{ $t('Dato' )}}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4 text-end">
                                    <span class="fw-bold">{{ invoiceDate }}</span>
                                </div>
                            </div>
                            <div class="form-group row justify-content-between align-bottom mb-4">
                                <div class="col-md-8 col-6">
                                    <label class="my-md-0">{{ $t('Forfallsdato' )}}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4 text-end">
                                    <span class="fw-bold">{{ invoiceDueDate }}</span>
                                </div>
                            </div>
                            <div class="form-group row justify-content-between align-bottom">
                                <div class="col-md-8 col-6">
                                    <label class="mt-0 my-md-0">{{ $t('Totalt beløp') }}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4">
                                    <input type="text" disabled :value="invoiceTotalAmountFormatted" class="form-control text-end">
                                </div>
                            </div>
                            <div class="form-group row justify-content-between align-bottom">
                                <div class="col-md-8 col-6">
                                    <label class="mt-0 my-md-0">{{ amountLabel }}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4">
                                    <input type="text" disabled :value="invoiceDueAmountFormatted" class="form-control text-end">
                                </div>
                            </div>
                            <div class="form-group row justify-content-between align-bottom">
                                <div class="col-md-8 col-6">
                                    <label class="mt-0 my-md-0">{{ $t('Beløp å betale') }}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4">
                                    <input type="text" disabled :value="invoiceDueAmount" class="form-control text-end">
                                </div>
                            </div>
                        </div>
                        <div v-if="!isInvoice && payInvoiceableLines">
                            <div class="row">
                                <div class="col-2">
                                    <div class="card-body text-elev-blue">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="card-icon">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12H12m-8.25 5.25h16.5" />
                                        </svg>
                                    </div>
                                </div>
                                <div class="col-10">
                                    <h5 class="card-title text-xs">{{ $t('Ufakturerte varelinjer') }}</h5>
                                    <p class="text-2xl mb-0"><strong>{{ accountSummary.not_invoiced_amount_formatted }}</strong></p>
                                </div>
                            </div>
                            <div class="row" style="margin-left: -20px; margin-right: -20px;">
                                <NotInvoicedLines :amount="accountSummary.not_invoiced_amount" :sales="accountSummary.not_invoiced_lines" :amountFormatted="accountSummary.not_invoiced_amount_formatted"></NotInvoicedLines>
                            </div>
                            <div class="hr-wrapper mb-3">
                                <hr>
                            </div>
                            <div class="form-group row justify-content-between align-bottom">
                                <div class="col-md-8 col-6">
                                    <label class="mt-0 my-md-2 ml-md-3">{{ $t('Beløp å betale') }}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4">
                                    <input type="text" disabled :value="accountSummary.not_invoiced_amount" class="form-control text-end">
                                </div>
                            </div>
                            <div class="hr-wrapper mb-3">
                                <hr>
                            </div>
                        </div>
                        <div v-if="!isInvoice && !payInvoiceableLines">
                            <div class="form-group row justify-content-between align-bottom" v-if="balanceAmount > 0">
                                <div class="col-md-8 col-6">
                                    <label class="mt-0 my-md-0">{{ amountLabel }}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4">
                                    <input type="text" disabled :value="formattedBalanceAmount" class="form-control text-end">
                                </div>
                            </div>
                            <div class="form-group row justify-content-between align-bottom">
                                <div class="col-md-8 col-6">
                                    <label class="mt-0 my-md-0">{{ $t('Ditt innskudd:') }}</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4">
                                    <input type="number" :value="sum" @keypress="validateValue" @change="setSum" @keyup="setSum" @blur="setSum" min="0" :max="maxSum" class="form-control text-end">
                                </div>
                            </div>
                            <div class="form-group row justify-content-between align-bottom mb-4" v-show="transactionFee > 0">
                                <div class="col-md-8 col-6">
                                    <label class="mt-0 my-md-0">{{ $t('Straksbetaling') }}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4 text-end">
                                    {{transactionFeeFormatted}}%
                                </div>
                            </div>

                            <div class="form-group row justify-content-between align-bottom" v-show="transactionFee > 0">
                                <div class="col-md-8 col-6">
                                    <label class="mt-0 my-md-0">{{ $t('Totalsum til {officeName}', {officeName})}}:</label>
                                </div>
                                <div class="col-6 col-lg-3 col-md-4">
                                    <input type="text" disabled :value="sumIncludingFee" class="form-control text-end">
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-12" v-if="!qrCodeImage">
                            <div class="text-center">
                                <button class="btn btn-primary btn-block btn-inline-block-md fw-bold" @click.prevent="submit" :disabled="isLoading">
                                    <span class="me-1">{{ $t('Trykk her for å betale med ') }}</span>
                                    <img :src="$window.enforceCDN('/tabselev/img/swish/swish-logo-secondary-dark-bg-2025.svg')" width="100px">
                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-show="isLoading"></span>
                                </button>
                            </div>
                        </div>

                        <div class="col-sm-12" v-if="qrCodeImage" >
                            <div class="d-flex justify-content-center">
                                <div class="center-block">
                                    <div>{{ $t('Åpne Swish - appen og scan koden') }}</div>
                                    <img alt="Swish QR-code" v-bind:src="qrCodeImage"/>
                                </div>
                            </div>
                        </div>
                        <div class="col-sm-12 mt-3">
                            <div class="row justify-content-center">
                                <div class="center-block">
                                    <div class="alert alert-info text-center" v-if="hasFullBookingTerms">
                                        <p class="mb-0"><small>{{ $t("Ved å gjennomføre denne betalingen godtar du våre bestillingsvilkår") }}:
                                            <br> <a class="text-underline" href="/booking-terms" target="_blank">{{ $t("Les fullstendige vilkår") }} <i class="fa fa-link"></i></a></small></p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <PaymentResponse :orderId="orderId"></PaymentResponse>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
    select {text-align-last: right;}
    option {direction: rtl;}
    label {
        word-break: normal;
        hyphens: auto;
    }
</style>
<script setup>
import { ref, computed } from 'vue'
import Store from '../../state/paymentStore'
import PaymentResponse from '../nosession/PaymentResponse'
import NotInvoicedLines from './NotInvoicedLines'
import { toMoney } from "../../lib/format"

const state = Store.state

const amountLabel = computed(() => Store.getBalanceAmountLabel())
const balanceAmount = computed(() => Store.getBalanceAmount())
const formattedBalanceAmount = computed(() => {
    if (isInvoice.value) {
        return invoiceDueAmountFormatted.value
    }
    return toMoney(balanceAmount.value)
})

const transactionFee = computed(() => Store.getTransactionFee())
const transactionFeeFormatted = computed(() => Store.getTransactionFee().toString().replace('.', ','))

const sum = computed(() => Store.getSum())
const maxSum = computed(() => Store.getMaxSum())
const sumIncludingFee = computed(() => Store.getSumIncludingFee())
const isLoading = computed(() => Store.getLoading())
const qrCodeImage = computed(() => {
    if (Store.getQrCode() !== null){
        return 'data:image/png;base64,' + Store.getQrCode()
    }
})
const hasFullBookingTerms = computed(() => Store.hasBookingTerms())
const isInvoice = computed(() => Store.getAccountOrderHistoryId() > 0)
const accountOrderHistory = computed(() => Store.getAccountOrderHistory())
const invoiceTypeAndOrderNumber = computed(() => accountOrderHistory.value.order_label + ' #' + accountOrderHistory.value.order_number)
const invoiceTotalAmountFormatted = computed(() => accountOrderHistory.value.amount_formatted)
const invoiceDueAmount = computed(() => accountOrderHistory.value.amount_due)
const invoiceDueAmountFormatted = computed(() => accountOrderHistory.value.amount_due_formatted)
const invoiceDate = computed(() => accountOrderHistory.value.order_date)
const invoiceDueDate = computed(() => accountOrderHistory.value.order_due_date)
const payInvoiceableLines = computed(() => Store.payInvoiceableLines())
const accountSummary = computed(() => Store.getAccountSummary())
const paymentCompleted = computed(() => Store.getPaymentStatusCompleted())
const orderId = computed(() => Store.getOrderId())

const validateValue = (event) => {
    if ([',','.','-'].indexOf(event.key) !== -1) {
        event.preventDefault()
    }
}
const setSum = (event) => {
    if (parseInt(event.target.value) > maxSum.value) {
        event.target.value = maxSum.value
    }
    Store.setSum(event.target.value)
}
const submit = () => {
    Store.submitSwish()
}
</script>
